import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select, Switch,
  Upload, App as AntdApp, Drawer, Tooltip, Empty, Popconfirm,
} from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, FileEdit, Trash2, Download, RefreshCw, FileText,
  Upload as UploadIcon, Eye, Variable, Copy,
} from 'lucide-react';
import dayjs from 'dayjs';
import { templateService, TemplateField, TemplateListItem, TemplateDetail } from '@/services/templateService';
import { departmentService } from '@/services/categoryService';
import { documentService } from '@/services/documentService';
import { CategoryTreeSelect } from '@/components/documents/CategoryTreeSelect';
import { apiClient } from '@/services/apiClient';

export function TemplatesAdminPage() {
  const { message, modal } = AntdApp.useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [duplicatingTemplate, setDuplicatingTemplate] = useState<TemplateListItem | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templateService.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templateService.delete(id),
    onSuccess: () => {
      message.success('Đã xóa mẫu');
      qc.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Xóa thất bại'),
  });

  const handleDownload = async (t: TemplateListItem) => {
    try {
      const res = await apiClient.get(`/templates/${t.id}/download`, { responseType: 'blob' });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t.code}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error('Không tải được file');
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <span>
            <FileText size={18} style={{ verticalAlign: -3, marginRight: 6 }} />
            Quản lý mẫu văn bản
          </span>
        }
        extra={
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            Thêm mẫu mới
          </Button>
        }
      >
        <p style={{ color: '#7d8a98', marginBottom: 16, fontSize: 13 }}>
          Upload file Word (.docx) có placeholder <code>{`{{tên_field}}`}</code> để tạo mẫu cho nhân viên dùng. Xem hướng dẫn cú pháp placeholder trong file <code>HUONG-DAN-TAO-MAU-WORD.md</code>.
        </p>

        <Table
          rowKey="id"
          dataSource={templates}
          loading={isLoading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: <Empty description="Chưa có mẫu nào — bấm 'Thêm mẫu mới'" /> }}
          columns={[
            {
              title: 'Mã', dataIndex: 'code', width: 110,
              render: (v) => <code style={{ background: '#f0f7ff', padding: '2px 6px', borderRadius: 3 }}>{v}</code>,
            },
            {
              title: 'Tên mẫu', dataIndex: 'name',
              render: (v, r) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{v}</div>
                  {r.description && (
                    <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 2 }}>{r.description}</div>
                  )}
                </div>
              ),
            },
            {
              title: 'Loại tài liệu', dataIndex: 'categoryName', width: 140,
              render: (v) => v ?? <span style={{ color: '#bfbfbf' }}>—</span>,
            },
            {
              title: 'Khoa/Phòng', dataIndex: 'departmentName', width: 140,
              render: (v) => v ?? <Tag color="blue">Công khai</Tag>,
            },
            {
              title: 'Số trường', dataIndex: 'fieldCount', width: 100, align: 'center',
              render: (v) => <Tag icon={<Variable size={11} />}>{v}</Tag>,
            },
            {
              title: 'Lượt dùng', dataIndex: 'useCount', width: 90, align: 'center',
              render: (v) => v > 0 ? <strong>{v}</strong> : <span style={{ color: '#bfbfbf' }}>0</span>,
            },
            {
              title: 'Trạng thái', dataIndex: 'isActive', width: 110,
              render: (v) => v ? <Tag color="success">Đang dùng</Tag> : <Tag>Tạm tắt</Tag>,
            },
            {
              title: 'Cập nhật', dataIndex: 'updatedAt', width: 140,
              render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm'),
            },
            {
              title: 'Thao tác', key: 'action', width: 180, fixed: 'right',
              render: (_, r) => (
                <Space size={4}>
                  <Tooltip title="Visual Builder (sửa form)">
                    <Button size="small" type="primary" ghost
                      icon={<Variable size={13} />}
                      onClick={() => navigate(`/admin/templates/${r.id}/builder`)} />
                  </Tooltip>
                  <Tooltip title="Sửa metadata">
                    <Button size="small" icon={<FileEdit size={13} />} onClick={() => setEditingId(r.id)} />
                  </Tooltip>
                  <Tooltip title="Sao chép sang mẫu mới">
                    <Button size="small" icon={<Copy size={13} />} onClick={() => setDuplicatingTemplate(r)} />
                  </Tooltip>
                  <Tooltip title="Tải file Word">
                    <Button size="small" icon={<Download size={13} />} onClick={() => handleDownload(r)} />
                  </Tooltip>
                  <Popconfirm
                    title={`Xóa mẫu "${r.name}"?`}
                    description="Lưu ý: dữ liệu PDF đã ký từ mẫu này không bị ảnh hưởng."
                    onConfirm={() => deleteMutation.mutate(r.id)}
                    okText="Xóa"
                    cancelText="Hủy"
                    okButtonProps={{ danger: true }}
                  >
                    <Button size="small" danger icon={<Trash2 size={13} />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          scroll={{ x: 1200 }}
        />
      </Card>

      {createOpen && (
        <CreateTemplateModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            qc.invalidateQueries({ queryKey: ['templates'] });
          }}
        />
      )}

      {editingId && (
        <EditTemplateDrawer
          id={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['templates'] });
          }}
        />
      )}

      {duplicatingTemplate && (
        <DuplicateTemplateModal
          source={duplicatingTemplate}
          onClose={() => setDuplicatingTemplate(null)}
          onDuplicated={(newTemplateId) => {
            setDuplicatingTemplate(null);
            qc.invalidateQueries({ queryKey: ['templates'] });
            // Optionally mở Builder ngay cho mẫu mới sao chép
            navigate(`/admin/templates/${newTemplateId}/builder`);
          }}
        />
      )}
    </div>
  );
}

// =============================================================
// Duplicate modal
// =============================================================
function DuplicateTemplateModal({ source, onClose, onDuplicated }: {
  source: TemplateListItem;
  onClose: () => void;
  onDuplicated: (newId: string) => void;
}) {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const newTpl = await templateService.duplicate(
        source.id,
        values.newCode.trim().toUpperCase(),
        values.newName.trim(),
      );
      message.success(`Đã sao chép thành mẫu "${newTpl.name}". Đang mở Visual Builder...`);
      onDuplicated(newTpl.id);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Sao chép thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      title={`Sao chép mẫu: ${source.name}`}
      width={520}
      onOk={() => form.submit()}
      okText="Sao chép & mở Builder"
      okButtonProps={{ loading, icon: <Copy size={13} /> }}
      destroyOnClose
    >
      <p style={{ color: '#7d8a98', fontSize: 13, marginBottom: 16 }}>
        Sẽ tạo bản sao của mẫu này với mã + tên mới. File Word, schema form,
        và toàn bộ cấu hình block sẽ được giữ nguyên — bạn có thể chỉnh sửa sau.
      </p>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          newCode: source.code + '_COPY',
          newName: source.name + ' (bản sao)',
        }}
      >
        <Form.Item
          label="Mã mẫu mới"
          name="newCode"
          rules={[
            { required: true, message: 'Bắt buộc' },
            { pattern: /^[A-Z0-9_]+$/, message: 'Chỉ chữ HOA, số, gạch dưới' },
          ]}
          extra={<>Mã hiện tại: <code>{source.code}</code></>}
        >
          <Input placeholder="VD: DNTT_V2" maxLength={50} style={{ textTransform: 'uppercase' }} />
        </Form.Item>

        <Form.Item
          label="Tên mẫu mới"
          name="newName"
          rules={[{ required: true, message: 'Bắt buộc' }]}
        >
          <Input maxLength={200} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// =============================================================
// Create modal
// =============================================================
function CreateTemplateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const { data: categories = [] } = useQuery({
    queryKey: ['document-categories'],
    queryFn: () => documentService.listCategories(),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentService.list(),
  });

  const handleSubmit = async (values: any) => {
    if (!file) {
      message.error('Vui lòng chọn file mẫu .docx');
      return;
    }
    setLoading(true);
    try {
      await templateService.create({
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        description: values.description?.trim(),
        categoryId: values.categoryId,
        departmentId: values.departmentId,
        isActive: values.isActive ?? true,
        file,
      });
      message.success('Tạo mẫu thành công');
      onCreated();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Tạo mẫu thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      title="Thêm mẫu văn bản mới"
      width={580}
      onOk={() => form.submit()}
      okText="Tạo mẫu"
      okButtonProps={{ loading }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isActive: true }}
      >
        <Form.Item
          label="Mã mẫu" name="code"
          rules={[
            { required: true, message: 'Bắt buộc' },
            { pattern: /^[A-Z0-9_]+$/, message: 'Chỉ chữ HOA, số, gạch dưới' },
          ]}
          extra="VD: DNTT, HDLD, TTRINH"
        >
          <Input placeholder="DNTT" maxLength={50} style={{ textTransform: 'uppercase' }} />
        </Form.Item>

        <Form.Item label="Tên mẫu" name="name" rules={[{ required: true, message: 'Bắt buộc' }]}>
          <Input placeholder="VD: Giấy đề nghị thanh toán" maxLength={200} />
        </Form.Item>

        <Form.Item label="Mô tả" name="description">
          <Input.TextArea rows={2} placeholder="Mô tả ngắn về mẫu, dùng khi nào..." maxLength={500} />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Loại tài liệu" name="categoryId">
            <CategoryTreeSelect categories={categories as any} placeholder="(không chọn)" />
          </Form.Item>
          <Form.Item label="Khoa/Phòng" name="departmentId" extra="Để trống = công khai">
            <Select allowClear placeholder="Tất cả khoa" options={(departments as any[]).map(d => ({ value: d.id, label: d.name }))} />
          </Form.Item>
        </div>

        <Form.Item label="File mẫu .docx" required>
          <Upload.Dragger
            accept=".docx"
            maxCount={1}
            beforeUpload={(f) => {
              if (f.size > 20 * 1024 * 1024) {
                message.error('File quá lớn (max 20MB)');
                return Upload.LIST_IGNORE;
              }
              setFile(f);
              return false;
            }}
            onRemove={() => setFile(null)}
            fileList={file ? [{ uid: '-1', name: file.name, status: 'done' as const }] : []}
          >
            <p style={{ marginBottom: 8 }}>
              <UploadIcon size={32} style={{ color: '#6699CC' }} />
            </p>
            <p>Kéo thả hoặc bấm để chọn file .docx</p>
            <p style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
              Tối đa 20MB. File phải có placeholder <code>{`{{tên_field}}`}</code>
            </p>
          </Upload.Dragger>
        </Form.Item>

        <Form.Item label="Kích hoạt ngay" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// =============================================================
// Edit drawer — sửa metadata + xem các field đã parse
// =============================================================
function EditTemplateDrawer({ id, onClose, onSaved }: {
  id: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [replaceFile, setReplaceFile] = useState<File | null>(null);

  const { data: detail, isLoading, refetch } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getById(id),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['document-categories'],
    queryFn: () => documentService.listCategories(),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentService.list(),
  });

  const updateMutation = useMutation({
    mutationFn: (values: any) => templateService.update(id, values),
    onSuccess: () => {
      message.success('Đã lưu');
      onSaved();
      refetch();
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Lưu thất bại'),
  });

  const replaceFileMutation = useMutation({
    mutationFn: (f: File) => templateService.replaceFile(id, f),
    onSuccess: () => {
      message.success('Đã thay file. Các field đã được parse lại.');
      setReplaceFile(null);
      qc.invalidateQueries({ queryKey: ['templates'] });
      refetch();
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Thay file thất bại'),
  });

  const handleSubmit = (values: any) => {
    // Lấy field overrides từ form
    const fieldOverrides: TemplateField[] = (detail?.fields ?? []).map(f => ({
      ...f,
      label: values[`label_${f.name}`] ?? f.label,
      required: values[`required_${f.name}`] ?? f.required,
    }));

    updateMutation.mutate({
      name: values.name,
      description: values.description,
      categoryId: values.categoryId,
      departmentId: values.departmentId,
      isActive: values.isActive,
      fieldOverrides,
    });
  };

  return (
    <Drawer
      title={detail ? `Sửa mẫu: ${detail.name}` : 'Đang tải...'}
      open
      onClose={onClose}
      width={680}
      extra={
        <Space>
          <Button icon={<Variable size={13} />}
            onClick={() => {
              onClose();
              window.location.href = `/admin/templates/${id}/builder`;
            }}>
            Mở Visual Builder
          </Button>
          <Button type="primary" loading={updateMutation.isPending} onClick={() => form.submit()}>
            Lưu
          </Button>
        </Space>
      }
    >
      {isLoading || !detail ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Đang tải...</div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            name: detail.name,
            description: detail.description,
            categoryId: detail.categoryId,
            departmentId: detail.departmentId,
            isActive: detail.isActive,
            ...Object.fromEntries(detail.fields.flatMap(f => [
              [`label_${f.name}`, f.label],
              [`required_${f.name}`, f.required],
            ])),
          }}
        >
          <Form.Item label="Mã" extra="Mã không thể đổi sau khi tạo">
            <Input disabled value={detail.code} />
          </Form.Item>

          <Form.Item label="Tên mẫu" name="name" rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>

          <Form.Item label="Mô tả" name="description">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item label="Loại tài liệu" name="categoryId">
              <CategoryTreeSelect categories={categories as any} placeholder="(không chọn)" />
            </Form.Item>
            <Form.Item label="Khoa/Phòng" name="departmentId">
              <Select allowClear placeholder="Công khai" options={(departments as any[]).map(d => ({ value: d.id, label: d.name }))} />
            </Form.Item>
          </div>

          <Form.Item label="Đang kích hoạt" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>

          <div style={{ marginTop: 24, marginBottom: 12, fontWeight: 600, fontSize: 13 }}>
            Thay file mẫu .docx
          </div>
          <Space style={{ marginBottom: 24 }}>
            <span style={{ fontSize: 12, color: '#7d8a98' }}>
              File hiện tại: <code>{detail.docxFilename}</code> ({(detail.docxSize / 1024).toFixed(1)} KB)
            </span>
          </Space>
          <Upload
            accept=".docx"
            maxCount={1}
            beforeUpload={(f) => {
              setReplaceFile(f);
              return false;
            }}
            onRemove={() => setReplaceFile(null)}
            fileList={replaceFile ? [{ uid: '-1', name: replaceFile.name, status: 'done' as const }] : []}
          >
            <Button icon={<RefreshCw size={13} />}>Chọn file mới</Button>
          </Upload>
          {replaceFile && (
            <Button
              type="primary"
              size="small"
              style={{ marginTop: 8 }}
              loading={replaceFileMutation.isPending}
              onClick={() => replaceFileMutation.mutate(replaceFile)}
            >
              Upload & parse lại fields
            </Button>
          )}

          <div style={{ marginTop: 32, marginBottom: 12, fontWeight: 600, fontSize: 13 }}>
            Các trường nhập liệu ({detail.fields.length})
          </div>
          {detail.fields.length === 0 ? (
            <Empty description="File này không có placeholder nào" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {detail.fields.map(f => (
                <div key={f.name} style={{
                  border: '1px solid #eceef1', borderRadius: 6,
                  padding: 10, background: f.type === 'auto' ? '#f0f7ff' : '#fafbfc',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 3, fontSize: 12 }}>
                      {`{{${f.name}${f.type !== 'text' && f.type !== 'auto' ? `:${f.type}` : ''}}}`}
                    </code>
                    <Tag color={typeColor(f.type)}>{typeLabel(f.type)}</Tag>
                  </div>
                  {f.type === 'auto' ? (
                    <div style={{ fontSize: 12, color: '#5b6875' }}>{f.label}</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 8 }}>
                      <Form.Item
                        label="Nhãn hiển thị"
                        name={`label_${f.name}`}
                        style={{ marginBottom: 0 }}
                      >
                        <Input size="small" />
                      </Form.Item>
                      <Form.Item
                        label="Bắt buộc"
                        name={`required_${f.name}`}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Switch size="small" />
                      </Form.Item>
                    </div>
                  )}
                  {f.options && f.options.length > 0 && (
                    <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                      Tùy chọn: {f.options.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Form>
      )}
    </Drawer>
  );
}

function typeColor(t: string): string {
  return ({
    text: 'default', textarea: 'default', number: 'orange', date: 'cyan',
    checkbox: 'purple', select: 'magenta', auto: 'blue',
  } as Record<string, string>)[t] ?? 'default';
}
function typeLabel(t: string): string {
  return ({
    text: 'Văn bản', textarea: 'Đoạn dài', number: 'Số', date: 'Ngày',
    checkbox: 'Tick', select: 'Chọn', auto: 'Tự động',
  } as Record<string, string>)[t] ?? t;
}
